import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../ui/alert-dialog";
import { Loader2, Sparkles, Trash2, TestTube, AlertTriangle, Check } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

export function DemoDataCard({ restaurantId, onRefresh }) {
  const [hasDemoData, setHasDemoData] = useState(false);
  const [generatingDemo, setGeneratingDemo] = useState(false);
  const [clearingDemo, setClearingDemo] = useState(false);

  useEffect(() => {
    if (!restaurantId) return;
    api.getDemoStatus(restaurantId)
      .then(res => setHasDemoData(res.has_demo_data))
      .catch(() => {});
  }, [restaurantId]);

  const handleGenerateDemo = async () => {
    setGeneratingDemo(true);
    try {
      const result = await api.generateDemoData(restaurantId);
      setHasDemoData(true);
      toast.success(
        `Datos demo generados: ${result.created.dishes} platos, ${result.created.sales} ventas, ${result.created.batch_recipes} elaboraciones base, ${result.created.production_logs} cierres producción, ${result.created.cash_sessions} sesiones de caja, ${result.created.invoices} facturas`,
        { duration: 6000 }
      );
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Error al generar datos demo");
    } finally {
      setGeneratingDemo(false);
    }
  };

  const handleClearDemo = async () => {
    setClearingDemo(true);
    try {
      await api.clearDemoData(restaurantId);
      setHasDemoData(false);
      toast.success("Datos demo eliminados correctamente");
      onRefresh?.();
    } catch (error) {
      toast.error(error.message || "Error al eliminar datos demo");
    } finally {
      setClearingDemo(false);
    }
  };

  return (
    <Card className="border-none shadow-soft border-2 border-dashed border-primary/30">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <TestTube className="w-5 h-5 text-primary" />
          Modo Demostración
          {hasDemoData && (
            <Badge className="ml-2 bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
              Activo
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Genera datos de prueba para explorar todas las funcionalidades de la plataforma.
          Podrás eliminarlos cuando quieras empezar con datos reales.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {hasDemoData ? (
          <div className="space-y-4">
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div>
                  <p className="font-medium text-yellow-700 dark:text-yellow-400">
                    Datos de demostración activos
                  </p>
                  <p className="text-sm text-yellow-600 dark:text-yellow-500">
                    Tu restaurante tiene datos de prueba. Incluyen proveedores, ingredientes,
                    clientes, un menú completo con platos, ventas simuladas de 30 días,
                    elaboraciones base, histórico de producción, sesiones de caja y alergenos.
                  </p>
                </div>
              </div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" className="w-full" disabled={clearingDemo} data-testid="clear-demo-btn">
                  {clearingDemo ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Eliminando...</>
                  ) : (
                    <><Trash2 className="w-4 h-4 mr-2" />Eliminar datos de demostración</>
                  )}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>¿Eliminar datos demo?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Esta acción eliminará todos los datos de demostración: proveedores,
                    ingredientes, clientes, menús, platos, ventas y recompensas marcados como DEMO.
                    Los datos reales que hayas creado se mantendrán.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                  <AlertDialogAction onClick={handleClearDemo} className="bg-destructive text-destructive-foreground">
                    Sí, eliminar datos demo
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-primary/5 rounded-lg">
              <p className="text-sm text-muted-foreground">Al generar datos demo se crearán:</p>
              <ul className="text-sm text-muted-foreground mt-2 space-y-1">
                {[
                  "4 proveedores de ejemplo",
                  "20 ingredientes con costes y stock",
                  "8 clientes del programa de fidelización",
                  "1 menú completo con 19 platos",
                  "Ventas simuladas de 30 días",
                  "4 recompensas de fidelización",
                  "4 elaboraciones base (Recetas Base) con caducidad",
                  "Recetas profesionales vinculadas a elaboraciones base",
                  "30 días de histórico de producción",
                  "30 sesiones de caja cerradas (para Cierres)",
                  "~30 facturas de proveedores (90 días, categorías reales)",
                  "Gastos contables vinculados a cada factura",
                  "Datos de alergenos detectados por IA",
                ].map((item) => (
                  <li key={item} className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <Button
              onClick={handleGenerateDemo}
              disabled={generatingDemo}
              className="w-full rounded-full"
              data-testid="generate-demo-btn"
            >
              {generatingDemo ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generando datos...</>
              ) : (
                <><Sparkles className="w-4 h-4 mr-2" />Generar datos de demostración</>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
